import React, { useState, useEffect } from 'react';
import { Shield, AlertCircle, Check } from 'lucide-react';
import { validatePassword } from '../../../../../utils/validation';

/**
 * Компонент для проверки надежности пароля
 */
const PasswordStrengthMeter = ({ passwordStrength, setPasswordStrength }) => {
  const [password, setPassword] = useState('');
  const [feedback, setFeedback] = useState({
    suggestions: [],
    warning: ''
  });

  useEffect(() => {
    if (password) {
      checkPassword(password);
    } else {
      setPasswordStrength(null);
      setFeedback({ suggestions: [], warning: '' });
    }
  }, [password]);

  // Функция проверки надежности пароля
  const checkPassword = (password) => {
    if (!password) {
      setPasswordStrength(null);
      return;
    }
    
    // Используем существующую функцию проверки пароля
    const result = validatePassword(
      password,
      8, // minLength
      true, // requireLower
      true, // requireUpper
      true, // requireDigit
      true  // requireSpecial
    );
    
    // Оценка эффективной энтропии пароля
    let alphabetSize = 0;
    if (/[a-z]/.test(password)) alphabetSize += 26;
    if (/[A-Z]/.test(password)) alphabetSize += 26;
    if (/[0-9]/.test(password)) alphabetSize += 10;
    if (/[^a-zA-Z0-9]/.test(password)) alphabetSize += 33;
    
    const entropy = Math.log2(Math.max(1, alphabetSize)) * password.length;
    
    // Определение оценки пароля на основе энтропии и проверок
    let score = 0;
    if (entropy < 28) score = 0; // Очень слабый
    else if (entropy < 56) score = 1; // Слабый
    else if (entropy < 80) score = 2; // Средний
    else if (entropy < 112) score = 3; // Сильный
    else score = 4; // Очень сильный
    
    // Корректировка на основе других факторов
    if (password.length < 8) score = Math.min(score, 1);
    if (password.length < 6) score = 0;
    
    // Дополнительная проверка на последовательности и повторения
    const hasSequences = /(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789|987|876|765|654|543|432|321|210)/i.test(password);
    const hasRepeats = /(.)\1{2,}/i.test(password); // 3+ одинаковых символа подряд
    
    if (hasSequences) score = Math.max(0, score - 1);
    if (hasRepeats) score = Math.max(0, score - 1);
    
    // Проверяем, содержит ли пароль распространенные слова
    const commonWords = ['password', 'admin', 'user', 'login', '12345', 'qwerty', 'welcome', 'парол'];
    if (commonWords.some(word => password.toLowerCase().includes(word))) {
      score = Math.max(0, score - 2);
    }
    
    // Формируем рекомендации для улучшения пароля
    const suggestions = [];
    const warnings = [];
    
    if (password.length < 8) {
      suggestions.push("Увеличьте длину пароля до 8 или более символов");
    }
    
    if (!result.hasLower) {
      suggestions.push("Добавьте строчные буквы (a-z)");
    }
    
    if (!result.hasUpper) {
      suggestions.push("Добавьте ЗАГЛАВНЫЕ буквы (A-Z)");
    }
    
    if (!result.hasDigit) {
      suggestions.push("Добавьте цифры (0-9)");
    }
    
    if (!result.hasSpecial) {
      suggestions.push("Добавьте специальные символы (!@#$%)");
    }
    
    if (hasSequences) {
      warnings.push("Избегайте последовательности символов (abc, 123)");
    }
    
    if (hasRepeats) {
      warnings.push("Избегайте повторяющихся символов (aaa, 111)");
    }
    
    if (commonWords.some(word => password.toLowerCase().includes(word))) {
      warnings.push("Избегайте использования распространенных слов");
    }
    
    // Формируем результат проверки
    setPasswordStrength({
      score,
      entropy,
      valid: result.valid,
      hasMinLength: result.hasMinLength,
      hasLower: result.hasLower,
      hasUpper: result.hasUpper,
      hasDigit: result.hasDigit,
      hasSpecial: result.hasSpecial,
      feedback: getStrengthFeedback(score)
    });
    
    setFeedback({
      suggestions,
      warnings
    });
  };
  
  // Получение общей оценки на основе силы пароля
  const getStrengthFeedback = (score) => {
    switch (score) {
      case 0:
        return "Очень слабый пароль. Его легко взломать. Попробуйте сделать пароль длиннее и сложнее.";
      case 1:
        return "Слабый пароль. Рекомендуется добавить специальные символы и увеличить длину.";
      case 2:
        return "Средний пароль. Добавьте больше символов разного типа для повышения надежности.";
      case 3:
        return "Хороший пароль. Он достаточно надежен для большинства сервисов.";
      case 4:
        return "Отличный пароль! Он очень надежен и устойчив к взлому.";
      default:
        return "";
    }
  };
  
  // Примерное время взлома пароля
  const getCrackTime = (entropy) => {
    if (!entropy) return "менее секунды";
    
    // Предположим, что компьютер может проверять 10 миллиардов паролей в секунду (10^10)
    const secondsToCrack = Math.pow(2, entropy) / Math.pow(10, 10);
    
    if (secondsToCrack < 1) return "менее секунды";
    if (secondsToCrack < 60) return `${Math.round(secondsToCrack)} секунд`;
    if (secondsToCrack < 3600) return `${Math.round(secondsToCrack / 60)} минут`;
    if (secondsToCrack < 86400) return `${Math.round(secondsToCrack / 3600)} часов`;
    if (secondsToCrack < 2592000) return `${Math.round(secondsToCrack / 86400)} дней`;
    if (secondsToCrack < 31536000) return `${Math.round(secondsToCrack / 2592000)} месяцев`;
    if (secondsToCrack < 315360000) return `${Math.round(secondsToCrack / 31536000)} лет`;
    if (secondsToCrack < 3153600000) return "более 100 лет";
    
    return "тысячи лет";
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Shield className="w-6 h-6 text-violet-500" />
        Проверка надежности пароля
      </h2>
      
      <div className="mb-6">
        <label htmlFor="passwordToCheck" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Введите пароль для проверки
        </label>
        <input
          type="text"
          id="passwordToCheck"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="form-input"
          placeholder="Введите пароль, который хотите проверить"
          autoComplete="off"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Пароль не сохраняется и не отправляется на сервер
        </p>
      </div>
      
      {password && passwordStrength && (
        <div className="space-y-6">
          {/* Индикатор надежности */}
          <div>
            <div className="flex justify-between mb-1 text-sm">
              <span className="text-gray-700 dark:text-gray-300">Надежность:</span>
              <span 
                className="font-medium" 
                style={{ 
                  color: ['#f87171', '#f87171', '#facc15', '#22c55e', '#10b981'][passwordStrength.score] 
                }}
              >
                {["Очень слабый", "Слабый", "Средний", "Хороший", "Отличный"][passwordStrength.score]}
              </span>
            </div>
            <div className="strength-meter">
              <div className={`strength-meter-fill strength-${passwordStrength.score}`}></div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              {passwordStrength.feedback}
            </p>
          </div>
          
          {/* Детали проверки */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Характеристики</h3>
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <div className={`mr-2 ${passwordStrength.hasMinLength ? 'text-green-500' : 'text-gray-400'}`}>
                    <Check className="w-4 h-4" />
                  </div>
                  <span className={passwordStrength.hasMinLength ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-500'}>
                    Минимум 8 символов
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  <div className={`mr-2 ${passwordStrength.hasLower ? 'text-green-500' : 'text-gray-400'}`}>
                    <Check className="w-4 h-4" />
                  </div>
                  <span className={passwordStrength.hasLower ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-500'}>
                    Строчные буквы
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  <div className={`mr-2 ${passwordStrength.hasUpper ? 'text-green-500' : 'text-gray-400'}`}>
                    <Check className="w-4 h-4" />
                  </div>
                  <span className={passwordStrength.hasUpper ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-500'}>
                    Заглавные буквы
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  <div className={`mr-2 ${passwordStrength.hasDigit ? 'text-green-500' : 'text-gray-400'}`}>
                    <Check className="w-4 h-4" />
                  </div>
                  <span className={passwordStrength.hasDigit ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-500'}>
                    Цифры
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  <div className={`mr-2 ${passwordStrength.hasSpecial ? 'text-green-500' : 'text-gray-400'}`}>
                    <Check className="w-4 h-4" />
                  </div>
                  <span className={passwordStrength.hasSpecial ? 'text-gray-700 dark:text-gray-300' : 'text-gray-500 dark:text-gray-500'}>
                    Специальные символы
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Статистика</h3>
              <div className="space-y-2">
                <div className="grid grid-cols-2 text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Длина:</span>
                  <span className="text-gray-900 dark:text-white font-medium">{password.length} символов</span>
                </div>
                <div className="grid grid-cols-2 text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Энтропия:</span>
                  <span className="text-gray-900 dark:text-white font-medium">{passwordStrength.entropy.toFixed(1)} бит</span>
                </div>
                <div className="grid grid-cols-2 text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Время взлома:</span>
                  <span className="text-gray-900 dark:text-white font-medium">
                    {getCrackTime(passwordStrength.entropy)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Рекомендации по улучшению */}
          {(feedback.suggestions.length > 0 || feedback.warnings.length > 0) && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2 flex items-center">
                <AlertCircle className="w-4 h-4 mr-2" />
                Как улучшить пароль
              </h3>
              {feedback.warnings.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-xs font-medium text-red-600 dark:text-red-400 mb-1">Проблемы:</h4>
                  <ul className="list-disc list-inside text-xs text-red-600 dark:text-red-400 space-y-1">
                    {feedback.warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
              )}
              {feedback.suggestions.length > 0 && (
                <div>
                  <h4 className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Рекомендации:</h4>
                  <ul className="list-disc list-inside text-xs text-blue-600 dark:text-blue-400 space-y-1">
                    {feedback.suggestions.map((suggestion, index) => (
                      <li key={index}>{suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          {/* Блок для сильных паролей */}
          {passwordStrength.score >= 3 && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-lg p-4">
              <h3 className="text-sm font-medium text-green-700 dark:text-green-300 mb-2 flex items-center">
                <Check className="w-4 h-4 mr-2" />
                Отличный пароль!
              </h3>
              <p className="text-xs text-green-600 dark:text-green-400">
                Ваш пароль соответствует современным требованиям безопасности и будет надежно защищать ваш аккаунт.
                {passwordStrength.score === 4 && ' Он очень устойчив даже к целенаправленным атакам.'}
              </p>
            </div>
          )}
        </div>
      )}
      
      {!password && (
        <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
          <Shield className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">
            Введите пароль выше, чтобы проверить его надежность
          </p>
        </div>
      )}
      
      <div className="mt-6 text-sm text-gray-600 dark:text-gray-400">
        <p>
          Надежный пароль должен:
        </p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Содержать не менее 8 символов</li>
          <li>Включать заглавные и строчные буквы</li>
          <li>Содержать цифры и специальные символы</li>
          <li>Не использовать личную информацию или распространенные слова</li>
          <li>Быть уникальным для каждого сервиса</li>
        </ul>
      </div>
    </div>
  );
};

export default PasswordStrengthMeter;